// src/components/Calendar/MeetingDetailsModal.jsx
import React from 'react';
import {
    Dialog, DialogTitle, DialogContent, Typography, Box, IconButton, Chip, Avatar, Stack, DialogActions, Button, Divider, useTheme
} from '@mui/material';
import {
    Close as CloseIcon, Event as EventIcon, People as PeopleIcon, Description as DescriptionIcon, Videocam as VideocamIcon, WorkOutline as WorkOutlineIcon
} from '@mui/icons-material';
import { format, parseISO } from 'date-fns';

const DetailItem = ({ icon, children }) => (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        <Box sx={{ flexShrink: 0, display: 'flex' }}>
            {icon}
        </Box>
        <Box>
            {children}
        </Box>
    </Box>
);

const MeetingDetailsModal = ({ open, onClose, props }) => {
    const theme = useTheme();
    const event = props?.event;
    if (!event) return null;

    const { summary, start, end, attendees, description, hangoutLink } = event;

    let displayDate = 'N/A';
    let displayTime = 'N/A';
    let jobTitle = 'Not specified';

    if (summary) {
        const forMatch = summary.match(/for (.*)/);
        if (forMatch && forMatch[1]) {
            jobTitle = forMatch[1];
        } else {
            const parts = summary.split(':');
            jobTitle = parts.length > 1 ? parts[1].trim() : summary;
        }
    }

    if (start?.dateTime) {
        const startDate = parseISO(start.dateTime);
        const endDate = end?.dateTime ? parseISO(end.dateTime) : null;
        displayDate = format(startDate, 'EEEE, MMMM d, yyyy');
        displayTime = `${format(startDate, 'p')}${endDate ? ` - ${format(endDate, 'p')}` : ''}`;
    } else if (start?.date) {
        const startDate = new Date(start.date.replace(/-/g, '/'));
        displayDate = format(startDate, 'EEEE, MMMM d, yyyy');
        displayTime = 'All-day';
    }

    return (
        <Dialog open={open} onClose={() => onClose(false)} fullWidth maxWidth="sm" PaperProps={{ sx: { borderRadius: '24px' } }}>
            <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 3,  }}>
                <Typography variant="h5" component="span" sx={{ fontWeight: 600 }}>
                    {summary || 'Meeting Details'}
                </Typography>
                <IconButton onClick={() => onClose(false)}><CloseIcon /></IconButton>
            </DialogTitle>
            <DialogContent dividers sx={{ p: 3 }}>
                <Stack spacing={3}>
                    <DetailItem icon={<EventIcon sx={{ color: 'text.secondary' }} />}>
                        <Typography variant="body1" sx={{ fontWeight: 500 }}>{displayDate}</Typography>
                        <Typography variant="body2" color="text.secondary">{displayTime}</Typography>
                    </DetailItem>
                    
                    <DetailItem icon={<WorkOutlineIcon sx={{ color: 'text.secondary' }} />}>
                        <Typography variant="body1" sx={{ fontWeight: 500 }}>Related To</Typography>
                        <Typography variant="body2" color="text.secondary">{jobTitle}</Typography>
                    </DetailItem>

                    {attendees && attendees.length > 0 && (
                        <Box>
                            <DetailItem icon={<PeopleIcon sx={{ color: 'text.secondary' }} />}>
                                <Typography variant="body1" sx={{ fontWeight: 500, mb: 1.5 }}>Attendees ({attendees.length})</Typography>
                            </DetailItem>
                             <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" sx={{ pl: 4.5 }}>
                                {attendees.map((attendee) => (
                                    <Chip
                                        key={attendee.email}
                                        avatar={<Avatar>{attendee.email.charAt(0).toUpperCase()}</Avatar>}
                                        label={attendee.self ? 'You' : attendee.email}
                                        variant="outlined"
                                    />
                                ))}
                            </Stack>
                        </Box>
                    )}

                    {description && (
                         <DetailItem icon={<DescriptionIcon sx={{ color: 'text.secondary' }} />}>
                            <Typography variant="body1" sx={{ fontWeight: 500 }}>Description</Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'pre-wrap' }}>{description}</Typography>
                        </DetailItem>
                    )}
                </Stack>
            </DialogContent>
             <DialogActions sx={{p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
                {hangoutLink && (
                    <Button
                        variant="contained"
                        startIcon={<VideocamIcon />}
                        href={hangoutLink}
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={{ borderRadius: '20px' }}
                    >
                        Join with Google Meet
                    </Button>
                )}
                <Button onClick={() => onClose(false)} sx={{ borderRadius: '20px' }}>Close</Button>
            </DialogActions>
        </Dialog>
    );
};

export default MeetingDetailsModal;